import React, { useState, useEffect, useRef } from 'react';
import {
  Home,
  HeadphonesIcon,
  HandHeart,
  Stethoscope,
  Activity,
  Users,
  ArrowLeft,
  Sparkles
} from 'lucide-react';

const Services = ({ openBookingModal }) => {
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  const servicesData = [
    {
      icon: <Activity className="w-7 h-7" />,
      title: "تحليل Inbody",
      description: "اعرف تفاصيل جسمك بدقة: نسبة الدهون، الكتلة العضلية، نسبة الماء والمزيد، خلال دقائق فقط.",
      accent: "blue",
      badge: null
    },
    {
      icon: <Home className="w-7 h-7" />,
      title: "الزيارة المنزلية",
      description: "ما تحتاج تطلع من بيتك. أخصائيونا يوصلولك أينما كنت لإجراء الفحوصات بكل راحة وسرية.",
      accent: "emerald",
      badge: "الأكثر طلبًا"
    },
    {
      icon: <HeadphonesIcon className="w-7 h-7" />,
      title: "خدمة عملاء 24 ساعة",
      description: "فريقنا متواجد للإجابة على استفساراتك في أي وقت، صباحًا أو مساءً، لأن راحتك أولويتنا.",
      accent: "violet",
      badge: null
    },
    {
      icon: <HandHeart className="w-7 h-7" />,
      title: "خدمة لغة الإشارة",
      description: "نوفر مترجم لغة الإشارة لشرح نتائج التحاليل بكل وضوح، لأن خدماتنا للجميع بلا استثناء.",
      accent: "amber",
      badge: null
    },
    {
      icon: <Stethoscope className="w-7 h-7" />,
      title: "استشارة وشرح النتائج",
      description: "احصل على استشارة طبية من متخصص قبل أو بعد التحاليل، ونشرح لك نتائجك بشكل مبسط.",
      accent: "rose",
      badge: null
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "قياس الضغط مجانًا",
      description: "تابع ضغطك بشكل منتظم ومجاني في أي من فروعنا، لأن الوقاية تبدأ من المتابعة الدورية.",
      accent: "teal",
      badge: null
    }
  ];

  const accentStyles = {
    blue:    { badge: "bg-blue-600 text-white", ring: "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white", border: "hover:border-blue-300" },
    emerald: { badge: "bg-emerald-600 text-white", ring: "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white", border: "hover:border-emerald-300" },
    violet:  { badge: "bg-violet-600 text-white", ring: "bg-violet-50 text-violet-600 group-hover:bg-violet-600 group-hover:text-white", border: "hover:border-violet-300" },
    amber:   { badge: "bg-amber-500 text-white", ring: "bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white", border: "hover:border-amber-300" },
    rose:    { badge: "bg-rose-600 text-white", ring: "bg-rose-50 text-rose-600 group-hover:bg-rose-600 group-hover:text-white", border: "hover:border-rose-300" },
    teal:    { badge: "bg-teal-600 text-white", ring: "bg-teal-50 text-teal-600 group-hover:bg-teal-600 group-hover:text-white", border: "hover:border-teal-300" }
  };

  // ظهور تدريجي للبطاقات عند دخولها الشاشة
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.idx);
            setVisibleCards((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
          }
        });
      },
      { threshold: 0.15 }
    );

    const cards = sectionRef.current?.querySelectorAll('[data-idx]');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 bg-gray-50 font-sans text-right overflow-hidden">
      <div className="container mx-auto px-4">
        {/* رأس القسم */}
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            <Sparkles className="w-4 h-4" />
            <span>خدمات مصممة حول راحتك</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            خدماتنا تغطي احتياجك
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            نقدم مجموعة شاملة من الخدمات المتخصصة لضمان حصولك على أفضل رعاية طبية، من أول لحظة تتواصل فيها معنا.
          </p>
        </div>

        {/* خط نبض بصري يفصل الرأس عن البطاقات — إشارة للطابع الطبي */}
        <div className="mb-14 max-w-md mx-auto opacity-70" aria-hidden="true">
          <svg viewBox="0 0 400 40" className="w-full h-8 text-blue-300" fill="none">
            <path
              d="M0 20 H140 L155 5 L170 35 L185 20 L200 20 L215 8 L225 32 L240 20 H400"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* شبكة الخدمات */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, index) => {
            const styles = accentStyles[service.accent];
            const isVisible = visibleCards.includes(index);
            return (
              <div
                key={service.title}
                data-idx={index}
                style={{ transitionDelay: isVisible ? `${(index % 3) * 90}ms` : '0ms' }}
                className={`group relative bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-500 ${styles.border} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                {service.badge && (
                  <span className={`absolute -top-3 right-6 text-xs font-bold px-3 py-1 rounded-full shadow-sm ${styles.badge}`}>
                    {service.badge}
                  </span>
                )}

                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300 ${styles.ring}`}>
                  {service.icon}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-5">
                  {service.description}
                </p>

                <button
                  onClick={openBookingModal}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-md px-1"
                >
                  <span>احجز هذه الخدمة</span>
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                </button>
              </div>
            );
          })}
        </div>

        {/* دعوة ختامية لاتخاذ إجراء */}
        <div className="mt-14 text-center">
          <button
            onClick={openBookingModal}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-10 rounded-full shadow-lg transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          >
            احجز موعدك الآن
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
